import { ENDPOINT } from '~/constants';

import { KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils/logging';

import {
  UserAnalyticsDetailsQueryParams,
  UserAnalyticsDetailsResponse,
} from './types';
import {
  zodUserAnalyticsDetailsQueryParamsSchema,
  zodUserAnalyticsDetailsResponseSchema,
} from './validations';

/**
 * Fetches the data for analytics details from the API with the given query parameters.
 *
 * @param queryParams - The query parameters for fetching the analytics details.
 * @param options - Additional options for the request (e.g., abort signal).
 * @returns The analytics details data including paginated table items.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/User-Activity/operation/get_user_activity_details_user_activity_details_post
 */
export const fetchUserAnalyticsDetails = async (
  queryParams: UserAnalyticsDetailsQueryParams,
  options?: KyRequestOptions,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.ANALYTICS.GET_USER_DETAILS(), {
      json: queryParams,
      validate: {
        body: zodUserAnalyticsDetailsQueryParamsSchema,
      },
      ...options,
    })
    .json<UserAnalyticsDetailsResponse>();

  // Validate the response and log any validation errors
  const validatedResponse =
    zodUserAnalyticsDetailsResponseSchema.safeParse(response);

  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'User analytics details API response has validation errors',
      error: {
        errors: validatedResponse?.error?.issues,
        errorMessage: validatedResponse?.error?.message,
        response,
      },
    });
  }

  return response;
};
